import { Directive, ElementRef, OnInit, OnDestroy, inject, Renderer2, input } from '@angular/core';

export interface AriaEnhancedConfig {
  /** Role for the element */
  role?: string;
  /** Label for the element */
  label?: string;
  /** Description for the element */
  description?: string;
  /** Whether element is expanded (for dropdowns, accordions) */
  expanded?: boolean;
  /** Whether element is selected (for options, tabs) */
  selected?: boolean;
  /** Whether element is pressed (for toggle buttons) */
  pressed?: boolean;
  /** Whether element is disabled */
  disabled?: boolean;
  /** Whether element is required */
  required?: boolean;
  /** Whether element is invalid */
  invalid?: boolean;
  /** Current value (for progress bars, sliders) */
  valuenow?: number;
  /** Minimum value */
  valuemin?: number;
  /** Maximum value */
  valuemax?: number;
  /** Text representation of current value */
  valuetext?: string;
  /** Level for headings */
  level?: number;
  /** Position in set */
  posinset?: number;
  /** Size of set */
  setsize?: number;
  /** Elements this element controls */
  controls?: string;
  /** Elements that describe this element */
  describedby?: string;
  /** Elements that label this element */
  labelledby?: string;
  /** Whether element owns popup */
  haspopup?: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
  /** Whether element is hidden */
  hidden?: boolean;
  /** Live region settings */
  live?: 'polite' | 'assertive' | 'off';
  /** Whether live region is atomic */
  atomic?: boolean;
  /** What changes should trigger live region updates */
  relevant?: 'additions' | 'removals' | 'text' | 'all';
}

@Directive({
  selector: '[gsAriaEnhanced]',
  standalone: true,
})
/**
 * Applies ARIA roles and attributes to elements using a flexible configuration
 * or granular inputs. Useful to enhance semantics for assistive technologies.
 */
export class AriaEnhancedDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  /** Configuration object or shorthand string (e.g., `"role=button expanded=true"`). */
  readonly gsAriaEnhanced = input<AriaEnhancedConfig | string>({});
  /** ARIA role for the element. */
  readonly gsAriaRole = input<string>();
  /** Accessible name (maps to `aria-label`). */
  readonly gsAriaLabel = input<string>();
  /** Additional description (maps to `aria-description`). */
  readonly gsAriaDescription = input<string>();
  /** Whether the element is expanded (e.g., accordion, disclosure). */
  readonly gsAriaExpanded = input<boolean>();
  /** Whether the element is selected (e.g., tab, option). */
  readonly gsAriaSelected = input<boolean>();
  /** Whether the element is pressed (for toggle buttons). */
  readonly gsAriaPressed = input<boolean>();
  /** Whether the element is disabled. */
  readonly gsAriaDisabled = input<boolean>();
  /** Whether the element is required. */
  readonly gsAriaRequired = input<boolean>();
  /** Whether the element is invalid. */
  readonly gsAriaInvalid = input<boolean>();
  /** Current numeric value for range widgets (`aria-valuenow`). */
  readonly gsAriaValuenow = input<number>();
  /** Minimum value for range widgets (`aria-valuemin`). */
  readonly gsAriaValuemin = input<number>();
  /** Maximum value for range widgets (`aria-valuemax`). */
  readonly gsAriaValuemax = input<number>();
  /** Human-readable text alternative for the current value (`aria-valuetext`). */
  readonly gsAriaValuetext = input<string>();
  /** Hierarchical level (e.g., heading level). */
  readonly gsAriaLevel = input<number>();
  /** Position of the item within a set (1-based). */
  readonly gsAriaPosinset = input<number>();
  /** Total number of items in the set. */
  readonly gsAriaSetsize = input<number>();
  /** ID reference(s) of elements this element controls (`aria-controls`). */
  readonly gsAriaControls = input<string>();
  /** ID reference(s) of elements that describe this element (`aria-describedby`). */
  readonly gsAriaDescribedby = input<string>();
  /** ID reference(s) of elements that label this element (`aria-labelledby`). */
  readonly gsAriaLabelledby = input<string>();
  /** Whether the element owns a popup, and its type (maps to `aria-haspopup`). */
  readonly gsAriaHaspopup = input<boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog'>();
  /** Whether the element is hidden from assistive technologies. */
  readonly gsAriaHidden = input<boolean>();
  /** Live region politeness setting (`aria-live`). */
  readonly gsAriaLive = input<'polite' | 'assertive' | 'off'>();
  /** Whether live region updates are atomic (`aria-atomic`). */
  readonly gsAriaAtomic = input<boolean>();
  /** Types of changes announced in a live region (`aria-relevant`). */
  readonly gsAriaRelevant = input<'additions' | 'removals' | 'text' | 'all'>();

  private config: AriaEnhancedConfig = {};
  private appliedAttributes = new Set<string>();

  /**
   * Lifecycle: Initialize directive by computing the initial config and applying attributes.
   */
  ngOnInit(): void {
    this.updateConfig();
    this.applyAttributes();
  }

  /**
   * Lifecycle: Recompute config and update attributes when inputs change.
   */
  ngOnChanges(): void {
    this.updateConfig();
    this.applyAttributes();
  }

  /**
   * Lifecycle: Remove any previously applied ARIA attributes.
   */
  ngOnDestroy(): void {
    this.appliedAttributes.forEach(attr => {
      this.renderer.removeAttribute(this.elementRef.nativeElement, attr);
    });
  }

  /**
   * Merge the main input (`gsAriaEnhanced`) with granular inputs into a single config object.
   */
  private updateConfig(): void {
    const gsAriaEnhanced = this.gsAriaEnhanced();
    if (typeof gsAriaEnhanced === 'string') {
      this.config = this.parseStringConfig(gsAriaEnhanced);
    } else {
      this.config = { ...gsAriaEnhanced };
    }

    const gsAriaRole = this.gsAriaRole();
    if (gsAriaRole !== undefined) this.config.role = gsAriaRole;
    const gsAriaLabel = this.gsAriaLabel();
    if (gsAriaLabel !== undefined) this.config.label = gsAriaLabel;
    const gsAriaDescription = this.gsAriaDescription();
    if (gsAriaDescription !== undefined) this.config.description = gsAriaDescription;
    const gsAriaExpanded = this.gsAriaExpanded();
    if (gsAriaExpanded !== undefined) this.config.expanded = gsAriaExpanded;
    const gsAriaSelected = this.gsAriaSelected();
    if (gsAriaSelected !== undefined) this.config.selected = gsAriaSelected;
    const gsAriaPressed = this.gsAriaPressed();
    if (gsAriaPressed !== undefined) this.config.pressed = gsAriaPressed;
    const gsAriaDisabled = this.gsAriaDisabled();
    if (gsAriaDisabled !== undefined) this.config.disabled = gsAriaDisabled;
    const gsAriaRequired = this.gsAriaRequired();
    if (gsAriaRequired !== undefined) this.config.required = gsAriaRequired;
    const gsAriaInvalid = this.gsAriaInvalid();
    if (gsAriaInvalid !== undefined) this.config.invalid = gsAriaInvalid;
    const gsAriaValuenow = this.gsAriaValuenow();
    if (gsAriaValuenow !== undefined) this.config.valuenow = gsAriaValuenow;
    const gsAriaValuemin = this.gsAriaValuemin();
    if (gsAriaValuemin !== undefined) this.config.valuemin = gsAriaValuemin;
    const gsAriaValuemax = this.gsAriaValuemax();
    if (gsAriaValuemax !== undefined) this.config.valuemax = gsAriaValuemax;
    const gsAriaValuetext = this.gsAriaValuetext();
    if (gsAriaValuetext !== undefined) this.config.valuetext = gsAriaValuetext;
    const gsAriaLevel = this.gsAriaLevel();
    if (gsAriaLevel !== undefined) this.config.level = gsAriaLevel;
    const gsAriaPosinset = this.gsAriaPosinset();
    if (gsAriaPosinset !== undefined) this.config.posinset = gsAriaPosinset;
    const gsAriaSetsize = this.gsAriaSetsize();
    if (gsAriaSetsize !== undefined) this.config.setsize = gsAriaSetsize;
    const gsAriaControls = this.gsAriaControls();
    if (gsAriaControls !== undefined) this.config.controls = gsAriaControls;
    const gsAriaDescribedby = this.gsAriaDescribedby();
    if (gsAriaDescribedby !== undefined) this.config.describedby = gsAriaDescribedby;
    const gsAriaLabelledby = this.gsAriaLabelledby();
    if (gsAriaLabelledby !== undefined) this.config.labelledby = gsAriaLabelledby;
    const gsAriaHaspopup = this.gsAriaHaspopup();
    if (gsAriaHaspopup !== undefined) this.config.haspopup = gsAriaHaspopup;
    const gsAriaHidden = this.gsAriaHidden();
    if (gsAriaHidden !== undefined) this.config.hidden = gsAriaHidden;
    const gsAriaLive = this.gsAriaLive();
    if (gsAriaLive !== undefined) this.config.live = gsAriaLive;
    const gsAriaAtomic = this.gsAriaAtomic();
    if (gsAriaAtomic !== undefined) this.config.atomic = gsAriaAtomic;
    const gsAriaRelevant = this.gsAriaRelevant();
    if (gsAriaRelevant !== undefined) this.config.relevant = gsAriaRelevant;
  }

  /**
   * Parse a simple space-separated shorthand string into a configuration object.
   * Example: "role=button expanded=true".
   * @param configString space-separated key=value pairs
   * @returns parsed configuration
   */
  private parseStringConfig(configString: string): AriaEnhancedConfig {
    const config: AriaEnhancedConfig = {};

    const parts = configString.split(' ');
    parts.forEach(part => {
      const [key, value] = part.split('=');
      if (!key || value === undefined) return;

      const boolValue = value === 'true' ? true : value === 'false' ? false : undefined;
      const numValue = !isNaN(Number(value)) ? Number(value) : undefined;

      switch (key.trim()) {
        case 'role':
          config.role = value;
          break;
        case 'expanded':
          config.expanded = boolValue ?? (value === 'true');
          break;
        case 'selected':
          config.selected = boolValue ?? (value === 'true');
          break;
        case 'pressed':
          config.pressed = boolValue ?? (value === 'true');
          break;
        case 'level':
          config.level = numValue ?? parseInt(value);
          break;
      }
    });

    return config;
  }

  /**
   * Apply the computed ARIA attributes to the host element, replacing any previously applied set.
   */
  private applyAttributes(): void {
    const element = this.elementRef.nativeElement;

    // Remove previously applied attributes
    this.appliedAttributes.forEach(attr => {
      this.renderer.removeAttribute(element, attr);
    });
    this.appliedAttributes.clear();

    // Apply new attributes
    this.setAttribute('role', this.config.role);
    this.setAttribute('aria-label', this.config.label);
    this.setAttribute('aria-description', this.config.description);
    this.setBooleanAttribute('aria-expanded', this.config.expanded);
    this.setBooleanAttribute('aria-selected', this.config.selected);
    this.setBooleanAttribute('aria-pressed', this.config.pressed);
    this.setBooleanAttribute('aria-disabled', this.config.disabled);
    this.setBooleanAttribute('aria-required', this.config.required);
    this.setBooleanAttribute('aria-invalid', this.config.invalid);
    this.setNumberAttribute('aria-valuenow', this.config.valuenow);
    this.setNumberAttribute('aria-valuemin', this.config.valuemin);
    this.setNumberAttribute('aria-valuemax', this.config.valuemax);
    this.setAttribute('aria-valuetext', this.config.valuetext);
    this.setNumberAttribute('aria-level', this.config.level);
    this.setNumberAttribute('aria-posinset', this.config.posinset);
    this.setNumberAttribute('aria-setsize', this.config.setsize);
    this.setAttribute('aria-controls', this.config.controls);
    this.setAttribute('aria-describedby', this.config.describedby);
    this.setAttribute('aria-labelledby', this.config.labelledby);
    this.setHaspopupAttribute(this.config.haspopup);
    this.setBooleanAttribute('aria-hidden', this.config.hidden);
    this.setAttribute('aria-live', this.config.live);
    this.setBooleanAttribute('aria-atomic', this.config.atomic);
    this.setAttribute('aria-relevant', this.config.relevant);
  }

  /**
   * Set a string attribute if it has a meaningful value.
   * @param name attribute name
   * @param value attribute value
   */
  private setAttribute(name: string, value: string | undefined): void {
    if (value !== undefined && value !== null && value !== '') {
      this.renderer.setAttribute(this.elementRef.nativeElement, name, value);
      this.appliedAttributes.add(name);
    }
  }

  /**
   * Set a boolean attribute from a boolean value.
   * @param name attribute name
   * @param value boolean value
   */
  private setBooleanAttribute(name: string, value: boolean | undefined): void {
    if (value !== undefined && value !== null) {
      this.renderer.setAttribute(this.elementRef.nativeElement, name, value.toString());
      this.appliedAttributes.add(name);
    }
  }

  /**
   * Set a numeric attribute from a number value.
   * @param name attribute name
   * @param value numeric value
   */
  private setNumberAttribute(name: string, value: number | undefined): void {
    if (value !== undefined && value !== null && !isNaN(value)) {
      this.renderer.setAttribute(this.elementRef.nativeElement, name, value.toString());
      this.appliedAttributes.add(name);
    }
  }

  /**
   * Convert `haspopup` to a valid ARIA attribute string and set it on the element.
   * @param value boolean or specific popup type
   */
  private setHaspopupAttribute(value: boolean | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | undefined): void {
    if (value !== undefined && value !== null) {
      const stringValue = value === true ? 'true' : value === false ? 'false' : value.toString();
      this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-haspopup', stringValue);
      this.appliedAttributes.add('aria-haspopup');
    }
  }
}
